import type { STDocument, STOpenFolderState } from '@st/sdk'
import { match, P } from '@st/pattern'
import { bundledDocumentView } from './bundled-document'
import { defaultDocumentView } from './default'
export { documentContract } from './document-contract'
import { engagementLetterDocumentView } from './engagement-letter'
import { taxReturnDeliveryDocumentView } from './tax-return-delivery'
import { selTaxReturnDeliveryPolicy } from './tax-return-delivery/state'
import type { STFolderModuleState, STFolderReadyState } from '../st-folder-types'
import type { DocumentView } from './types'

type DocumentViewerFolderState = Pick<
  STOpenFolderState,
  | 'documentTypes'
  | 'folderId'
  | 'engagementLetter'
  | 'taxReturnDelivery'
  | 'taxReturnDeliveryDrafts'
>

export type DocumentViewerState = {
  folderState: DocumentViewerFolderState
  deliveryCanEdit: boolean
  deliveryCanEditFields: boolean
}

type DocumentViewResolver = (
  state: DocumentViewerState,
  document: STDocument,
  baseView: DocumentView
) => DocumentView | undefined

const documentViewResolvers: DocumentViewResolver[] = [
  (state, document, baseView) =>
    taxReturnDeliveryDocumentView({
      groupTypes:
        state.folderState.documentTypes.find((type) => type.id === document.documentTypeId)
          ?.pageGroupTypes ?? [],
      document,
      publishedContract: state.folderState.taxReturnDelivery,
      drafts: state.folderState.taxReturnDeliveryDrafts,
      canEdit: state.deliveryCanEdit,
      canEditFields: state.deliveryCanEditFields,
      baseView
    }),
  (_state, document, baseView) =>
    bundledDocumentView({
      document,
      baseView
    }),
  (state, document, baseView) =>
    engagementLetterDocumentView({
      document,
      contract: state.folderState.engagementLetter,
      baseView
    })
]

/**
 * Selects the document's feature-specific controls.
 *
 * Resolution order is policy: TRD owns signing controls, bundled
 * documents own split controls, and engagement letters own their contract
 * banner. The default view is the fallback rather than another registry entry.
 * This is the only place that projects shared folder state into the narrow
 * inputs owned by each feature.
 */
export function resolveDocumentView(
  state: DocumentViewerState,
  document: STDocument
): DocumentView {
  const baseView = defaultDocumentView({
    document
  })

  for (const resolveView of documentViewResolvers) {
    const view = resolveView(state, document, baseView)
    if (view) {
      return view
    }
  }

  return baseView
}

export function documentViewerState(state: STFolderReadyState): DocumentViewerState {
  const policy = selTaxReturnDeliveryPolicy(state)
  return {
    folderState: {
      folderId: state.folderState.folderId,
      documentTypes: state.folderState.documentTypes,
      engagementLetter: state.folderState.engagementLetter,
      taxReturnDelivery: state.folderState.taxReturnDelivery,
      taxReturnDeliveryDrafts: state.folderState.taxReturnDeliveryDrafts
    },
    deliveryCanEdit: policy.canEdit,
    deliveryCanEditFields: policy.canEditFields
  }
}

export function selDocumentViewerState(state: STFolderModuleState): DocumentViewerState {
  return match(state)
    .with({ status: 'ready' }, documentViewerState)
    .otherwise(() => {
      throw new Error('Document viewer state requested before folder is ready')
    })
}

export type { DocumentView } from './types'
export type { DocumentContractKind } from './top-bar-types'
